import { HeartHandshake, Sparkles, ShieldCheck } from "lucide-react";
import { Reveal } from "@/lib/reveal";
import { SectionMarker } from "@/components/SectionMarker";

const AIGA_TAGS = ["Family", "Belonging", "Connection"];
const ENABLE_TAGS = ["Empowerment", "Independence", "Possibility"];

export const MeaningBehindName = () => {
  return (
    <section id="meaning" className="relative py-20 lg:py-32 overflow-hidden" data-testid="meaning-section">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-10 h-[360px] w-[360px] rounded-full bg-teal/10 blur-[140px]" />
        <div className="absolute right-1/4 bottom-10 h-[360px] w-[360px] rounded-full bg-aigablue/12 blur-[140px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <SectionMarker n="05" label="Our story" />
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            The Meaning Behind Our Name
          </h2>
          <p className="mt-4 font-display text-lg sm:text-xl text-slate-300 leading-relaxed">
            A name grounded in connection. A technology built for independence.
          </p>
        </Reveal>

        {/* Two complementary concepts */}
        <div className="mt-14 grid gap-5 md:grid-cols-2">
          <Reveal>
            <article className="relative h-full rounded-2xl glass p-7 sm:p-8" data-testid="meaning-aiga">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal/15" style={{ boxShadow: "0 0 22px -8px #00D2B4" }}>
                  <HeartHandshake className="h-6 w-6 text-teal" aria-hidden="true" />
                </span>
                <h3 className="font-display text-3xl font-extrabold tracking-tight text-white">Aiga</h3>
              </div>
              <ul className="mt-5 flex flex-wrap gap-2" aria-label="Aiga themes">
                {AIGA_TAGS.map((t) => (
                  <li key={t} className="rounded-full border border-teal/25 bg-teal/5 px-3 py-1 text-xs font-medium text-teal">
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-base text-slate-200 leading-relaxed">
                <span className="text-slatemuted">Aiga means family</span> — however it extends beyond
                immediate family to include the people who support you, stand alongside you, and form
                part of your wider community. It represents connection, belonging and collective
                support, recognising that accessibility is not experienced in isolation.
              </p>
            </article>
          </Reveal>

          <Reveal delay={0.08}>
            <article className="relative h-full rounded-2xl glass p-7 sm:p-8" data-testid="meaning-enable">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyan/15" style={{ boxShadow: "0 0 22px -8px #00F0FF" }}>
                  <Sparkles className="h-6 w-6 text-cyan" aria-hidden="true" />
                </span>
                <h3 className="font-display text-3xl font-extrabold tracking-tight text-white">Enable</h3>
              </div>
              <ul className="mt-5 flex flex-wrap gap-2" aria-label="Enable themes">
                {ENABLE_TAGS.map((t) => (
                  <li key={t} className="rounded-full border border-cyan/25 bg-cyan/5 px-3 py-1 text-xs font-medium text-cyan">
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-base text-slate-200 leading-relaxed">
                Enable means creating the conditions for people to participate, contribute and live
                with greater choice, confidence and independence. It is about removing barriers,
                providing the right tools and information, and supporting people to do things in ways
                that work for them.
              </p>
            </article>
          </Reveal>
        </div>

        {/* Combined key statement */}
        <Reveal delay={0.1}>
          <div className="mt-6 rounded-3xl bg-gradient-to-br from-teal/10 via-navy-700/40 to-aigablue/10 ring-1 ring-teal/20 p-8 sm:p-12 text-center" data-testid="meaning-together">
            <span className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-gradient-teal">
              Aiga Enable
            </span>
            <p className="mx-auto mt-4 max-w-3xl font-display text-xl sm:text-2xl font-semibold text-white leading-relaxed">
              Together, Aiga Enable represents our belief that people, families, communities and
              organisations can work together to make accessibility easier, more connected and more
              inclusive.
            </p>
          </div>
        </Reveal>

        {/* Connect the name to A-ID */}
        <Reveal delay={0.05}>
          <div className="mt-10 max-w-3xl">
            <p className="text-lg text-slate-200 leading-relaxed">
              A-ID puts that belief into practice — giving people greater control over their
              accessibility information and how it is shared.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-teal">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Empower Accessibility. Enable Independence. Reduce Risk.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
